/**
 * SummaryTab Component
 * TASK-397: Post-Meeting Tabbed Redesign
 *
 * Executive summary + FINSA banner + KYC extraction (onboarding only).
 * Reuses existing PostMeetingHub sub-components.
 */

import React from 'react';
import { Shield, AlertTriangle } from 'lucide-react';
import MeetingExecutiveSummary from '../PostMeetingHub/ExecutiveSummary';
import { KYCExtractionReview } from '../PostMeetingHub/KYCExtractionReview';

// ============================================================================
// Types
// ============================================================================

interface SummaryTabProps {
  sessionId: string;
  clientId: string;
  summary: string;
  timer: string;
  finsaStatus: 'advisory_minutes' | 'business_notes' | 'no_documentation';
  requiresClientDelivery: boolean;
  meetingType?: string;
  hasPendingKYC: boolean;
  onSummaryChange: (value: string) => void;
  onRegenerate: () => void;
  onKYCApproved: () => void;
}

// ============================================================================
// Component
// ============================================================================

const SummaryTab: React.FC<SummaryTabProps> = ({
  sessionId,
  clientId,
  summary,
  timer,
  finsaStatus,
  requiresClientDelivery,
  meetingType,
  hasPendingKYC,
  onSummaryChange,
  onRegenerate,
  onKYCApproved,
}) => {
  const [showKYCReview, setShowKYCReview] = React.useState(false);

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* FINSA compliance banner */}
      {finsaStatus !== 'no_documentation' && (
        <div className={`flex items-start gap-3 p-4 rounded-lg border ${
          finsaStatus === 'advisory_minutes'
            ? 'bg-amber-50 border-amber-200'
            : 'bg-blue-50 border-blue-200'
        }`}>
          <Shield className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
            finsaStatus === 'advisory_minutes' ? 'text-amber-500' : 'text-blue-500'
          }`} />
          <div>
            <p className={`text-sm font-medium ${
              finsaStatus === 'advisory_minutes' ? 'text-amber-800' : 'text-blue-800'
            }`}>
              {finsaStatus === 'advisory_minutes'
                ? 'FINSA Advisory Minutes Required'
                : 'Business Notes Generated'}
            </p>
            {requiresClientDelivery && (
              <p className="text-xs text-gray-600 mt-1">
                Documentation must be delivered to the client.
              </p>
            )}
          </div>
        </div>
      )}

      {/* KYC extraction banner (onboarding only) */}
      {meetingType === 'onboarding' && hasPendingKYC && (
        <div className="flex items-start gap-3 p-4 rounded-lg border bg-purple-50 border-purple-200">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-500" />
          <div className="flex-1">
            <p className="text-sm font-medium text-purple-800">
              KYC Data Extracted from Meeting
            </p>
            <p className="text-xs text-purple-600 mt-1">
              Review and approve the extracted KYC fields before they are saved.
            </p>
            <button
              onClick={() => setShowKYCReview(true)}
              className="mt-2 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 rounded-md hover:bg-purple-200 transition-colors"
            >
              Review KYC Data
            </button>
          </div>
        </div>
      )}

      {/* Executive Summary */}
      <MeetingExecutiveSummary
        summary={summary}
        timer={timer}
        sessionId={sessionId}
        onSummaryChange={onSummaryChange}
        onRegenerate={onRegenerate}
      />

      {/* KYC Extraction Review Modal */}
      {showKYCReview && clientId && (
        <KYCExtractionReview
          sessionId={sessionId}
          clientId={clientId}
          onApproved={() => {
            setShowKYCReview(false);
            onKYCApproved();
          }}
          onClose={() => setShowKYCReview(false)}
        />
      )}
    </div>
  );
};

export default SummaryTab;
